import { z } from "zod";
import type { McpServer } from "@modelcontextprotocol/sdk/server/mcp.js";
import type { CarrierProps, ToolScope } from "./types.js";
import {
  probeAll,
  rankTargets,
  isTargetId,
  TARGET_IDS,
  type TargetId,
} from "./cli/lib/deploy-targets.js";
import { buildSite, deploySite, loadStorefrontBrand } from "./cli/lib/site.js";
import {
  configureClerkInstance,
  provisionClerk,
  storefrontClerkUrls,
} from "./cli/lib/clerk.js";
import { mergeEnvLocal } from "./cli/lib/storefront-secrets.js";
import { clerkCliDeps } from "./cli/lib/clerk-cli.js";
import { formatProbes, repairPlanFor, verifyStorefront } from "./cli/lib/verify-storefront.js";
import { annotationsFor } from "./tool-annotations.js";
import { denyUnlessScoped } from "./scope-guard.js";

/**
 * Storefront deploy + Clerk tools.
 *
 * These run local processes (host CLIs) against a directory on the caller's
 * machine, so they are only meaningful over stdio. There is no mcp-server
 * mirror: none of these three names exists in the Worker's source or in its
 * ALL_TOOL_SCOPES, so npm is the only surface that ships them and nothing done
 * in apps/mcp-server can ever cover them.
 *
 * SCOPE
 * -----
 * All three declared a scope below and none enforced it. The table fed only the
 * `tools/list` filter, and hiding a tool is not enforcement —
 * `tool-list-scope-filter.ts` says so in its own header. `deploy_storefront`
 * stages CARRIER_API_KEY and CLERK_SECRET_KEY on a third-party deploy host and
 * `provision_storefront_clerk` creates a Clerk application, so both are `write`.
 *
 * The guards cannot currently refuse anybody: index.ts builds the one
 * CarrierProps on this surface with every scope hardcoded. They are a regression
 * fence for the day stdio derives scope from an API key or a login, at which
 * point an unguarded tool here becomes a privilege escalation with an install
 * command.
 */
export const STOREFRONT_DEPLOY_TOOL_SCOPES: Record<string, ToolScope> = {
  list_deploy_targets: "read",
  deploy_storefront: "write",
  provision_storefront_clerk: "write",
};

type TextResult = { content: Array<{ type: "text"; text: string }>; isError?: boolean };
const ok = (value: unknown): TextResult => ({
  content: [{ type: "text", text: JSON.stringify(value) }],
});
const err = (text: string): TextResult => ({ isError: true, content: [{ type: "text", text }] });

export function registerStorefrontDeployTools(server: McpServer, props: CarrierProps): void {
  server.registerTool(
    "list_deploy_targets",
    {
      title: "List Deploy Targets",
      description:
        "Report which storefront deploy hosts (Cloudflare, Vercel, Netlify, Fly) are installed, logged in, and ready on this machine, and which one a deploy would pick.",
      inputSchema: {
        dir: z.string().min(1).describe("Path to the scaffolded storefront directory"),
      },
      annotations: annotationsFor("list_deploy_targets", "read"),
    },
    async ({ dir }) => {
      const denied = denyUnlessScoped(props, "list_deploy_targets", STOREFRONT_DEPLOY_TOOL_SCOPES);
      if (denied) return denied;
      try {
        const statuses = await probeAll(dir);
        const ranked = rankTargets(statuses);
        return ok({
          targets: statuses,
          default: ranked[0]?.id ?? null,
          reason: ranked.length ? undefined : "No host is installed and logged in.",
        });
      } catch (e) {
        return err(e instanceof Error ? e.message : "target probe failed");
      }
    },
  );

  server.registerTool(
    "deploy_storefront",
    {
      title: "Deploy Storefront",
      description:
        "Build and deploy a scaffolded storefront to whichever host is available on this machine, then push its runtime secrets (CARRIER_API_KEY, CLERK_SECRET_KEY) to that host.",
      inputSchema: {
        dir: z.string().min(1).describe("Path to the scaffolded storefront directory"),
        target: z
          .enum(TARGET_IDS as unknown as [string, ...string[]])
          .optional()
          .describe("Force a host instead of auto-detecting"),
        name: z.string().optional().describe("Project/worker name (defaults from brand)"),
        skip_build: z.boolean().optional().describe("Deploy the existing build without rebuilding"),
        push_secrets: z
          .boolean()
          .optional()
          .describe("Stage runtime secrets on the host before deploy (default true)"),
        verify: z
          .boolean()
          .optional()
          .describe("Probe the deployed URL and report whether it actually serves (default true)"),
        domain: z
          .boolean()
          .optional()
          .describe("Route the brand domain to the deployment (Cloudflare provisions DNS + cert)"),
      },
      annotations: annotationsFor("deploy_storefront", "write"),
    },
    async ({ dir, target, name, skip_build, push_secrets, verify, domain }) => {
      const denied = denyUnlessScoped(props, "deploy_storefront", STOREFRONT_DEPLOY_TOOL_SCOPES);
      if (denied) return denied;
      try {
        if (target && !isTargetId(target)) return err(`Unknown target "${target}".`);
        const brand = await loadStorefrontBrand(dir, name ? { name } : undefined);
        const preferred = target as TargetId | undefined;

        if (!skip_build) {
          const ranked = rankTargets(await probeAll(dir));
          const buildFor = preferred ?? ranked[0]?.id ?? "cloudflare";
          const built = await buildSite(dir, buildFor);
          if (!built) return err(`Build failed for ${buildFor}. Run the build locally to see why.`);
        }

        const result = await deploySite(dir, brand, {
          preferred,
          pushSecrets: push_secrets !== false,
          customDomain: domain === true,
        });
        if (!result.ok) {
          return err(result.reason ?? "Deploy failed.");
        }

        // A deploy that was never checked is not a success worth reporting as
        // one — that is precisely how a 500-ing storefront got signed off before.
        const verification =
          verify !== false && result.url
            ? await verifyStorefront(result.url, dir, process.env)
            : undefined;

        return ok({
          deployed: true,
          target: result.target,
          project: result.projectName,
          url: result.url ?? null,
          secrets_staged: result.secrets?.pushed ?? [],
          secrets_failed: result.secrets?.failed ?? [],
          verified: verification ? verification.ok : null,
          diagnosis: verification?.ok === false ? verification.diagnosis : undefined,
          probes: verification ? formatProbes(verification.probes) : undefined,
          repair_hint:
            verification && !verification.ok
              ? (repairPlanFor(verification.diagnosis)?.note ?? verification.summary)
              : undefined,
          warning: result.secrets?.pushed.includes("CARRIER_API_KEY")
            ? undefined
            : "CARRIER_API_KEY was not staged — / and /shop will return 500 until it is set.",
        });
      } catch (e) {
        return err(e instanceof Error ? e.message : "deploy failed");
      }
    },
  );

  server.registerTool(
    "provision_storefront_clerk",
    {
      title: "Provision Storefront Clerk App",
      description:
        "Create a Clerk application (Platform API) or reuse Clerk keys already on this machine, write them into the storefront .env.local, and configure allowed origins and redirect URLs on the instance.",
      inputSchema: {
        dir: z.string().min(1).describe("Path to the scaffolded storefront directory"),
        name: z.string().optional().describe("Clerk application name (defaults from brand)"),
        production: z
          .boolean()
          .optional()
          .describe("Also create a production instance (Platform API only)"),
        no_create: z
          .boolean()
          .optional()
          .describe("Never create a new application; only reuse existing keys"),
        url: z.string().optional().describe("Deployed URL to whitelist as origin and redirect"),
      },
      annotations: annotationsFor("provision_storefront_clerk", "write"),
    },
    async ({ dir, name, production, no_create, url }) => {
      const denied = denyUnlessScoped(
        props,
        "provision_storefront_clerk",
        STOREFRONT_DEPLOY_TOOL_SCOPES,
      );
      if (denied) return denied;
      try {
        const brand = await loadStorefrontBrand(dir, name ? { name } : undefined);
        const result = await provisionClerk({
          name: name ?? brand.name,
          domain: brand.domain,
          production: production === true,
          noCreate: no_create === true,
          env: process.env,
          storefront: dir,
          cli: clerkCliDeps(),
        });
        if (!result.ok || !result.credentials) {
          return ok({
            provisioned: false,
            tier: result.tier,
            reason: result.reason,
            guidance: result.guidance ?? [],
          });
        }
        const written = await mergeEnvLocal(dir, {
          NEXT_PUBLIC_CLERK_PUBLISHABLE_KEY: result.credentials.publishableKey,
          CLERK_SECRET_KEY: result.credentials.secretKey,
        });
        const { allowedOrigins, redirectUrls } = storefrontClerkUrls(url, brand.domain);
        const configured = await configureClerkInstance(result.credentials.secretKey, {
          allowedOrigins,
          redirectUrls,
        });
        // Keys themselves are never returned — they are on disk and on the host.
        return ok({
          provisioned: true,
          tier: result.tier,
          application_id: result.credentials.applicationId ?? null,
          instance_id: result.credentials.instanceId ?? null,
          env_written: written,
          configured: configured.applied,
          configure_failures: configured.failed,
        });
      } catch (e) {
        return err(e instanceof Error ? e.message : "clerk provisioning failed");
      }
    },
  );
}
